import { FC, useEffect, useState } from "react";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import cargoTranslationsHelper from "../../../../languages/cargo-translations.helper";
import Row from "../../../../common/components/grid/row";
import Column from "../../../../common/components/grid/column";
import CardComponent from "../../../../common/components/card/card.component";
import FormFieldComponent from "../../../../common/components/form/field/form-field.component";
import ButtonComponent from "../../../../common/components/button/button.component";
import CargoTaxiContractAddFormData from "./common/types/cargo-taxi-contract-add-form-data";
import cargoTaxiContractAddFormHelper from "./common/cargo-taxi-contract-add-form.helper";
import { useNavigate, useParams } from "react-router-dom";
import CargoTaxiContractAddRouteParams from "../../common/routes/types/cargo-taxi-contract-add-route-params";
import NumericInputComponent from "../../../../common/components/form/input/numeric-input/numeric-input.component";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import cargoBreadcrumbsHelper from "../../common/breadcrumbs/cargo-breadcrumbs.helper";
import BooleanSelectComponent from "../../../../common/components/form/select/boolean/boolean-select.component";
import notificationService from "../../../../common/utils/notification/notification.service";
import cargoRoutesHelper from "../../common/routes/cargo-routes.helper";
import DateRangeInputComponent from "../../../../common/components/form/input/date-range/date-range-input.component";
import useForm from "../../../../common/components/form/use-form";
import useTaxiCorporationList from "../../../../common/services/taxi-corporation/list/use-taxi-corporation-list";
import useAbort from "../../../../common/hooks/use-abort";
import ContractsCargoTaxiAddParams from "../../../../common/services/contracts/cargo-taxi/add/contracts-cargo-taxi-add-params";
import BillingModelSingleSelectComponent from "../../../../common/components/form/select/billing-model/billing-model-single-select.component";
import TaxiCorporationSingleSelectComponent from "../../../../common/components/form/select/taxi-corporation/taxi-corporation-single-select.component";
import cargoTaxiContractAddParamsFactory from "./common/cargo-taxi-contract-add-params.factory";
import useCargoCompanyDetails from "../../../../common/services/cargo-company/cargo-company/details/use-cargo-company-details";
import contractsService from "../../../../common/services/contracts/contracts.service";

type CargoTaxiContractAddProps = {};

const CargoTaxiContractAddComponent: FC<CargoTaxiContractAddProps> = () => {
  const { cargoCompanyUuid } = useParams<CargoTaxiContractAddRouteParams>();
  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  const navigate = useNavigate();

  useDocumentTitle(documentTitleTranslations.taxiCargoContractAdd);

  const [cargoCompanyName, setCargoCompanyName] = useState("");

  const cargoCompany = useCargoCompanyDetails();
  const cargoCompanyAbort = useAbort();

  useEffect(() => {
    if (!cargoCompanyUuid) return;

    cargoCompany.load(
      {
        cargoCompanyUuid,
      },
      cargoCompanyAbort.signal,
    );

    return () => cargoCompanyAbort.revoke();
  }, [cargoCompanyUuid]);

  useEffect(() => {
    if (!cargoCompany.data) return;

    setCargoCompanyName(cargoCompany.data.displayName);
  }, [cargoCompany.data]);

  useEffect(() => {
    const breadcrumbs = cargoBreadcrumbsHelper.getTaxiContractAddBreadcrumbs({
      cargoCompanyUuid: cargoCompanyUuid!,
      cargoCompanyName,
    });

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, cargoCompanyName]);

  const translations = cargoTranslationsHelper.getTaxiContractAddTranslations();

  const form = useForm<CargoTaxiContractAddFormData>({
    emptyValues: cargoTaxiContractAddFormHelper.getEmptyFormData(),
    validationDefinition:
      cargoTaxiContractAddFormHelper.getValidationDefinition(),
  });

  const taxiCorporationList = useTaxiCorporationList();
  const taxiCorporationListAbort = useAbort();

  const cargoTaxiContractAddAbort = useAbort();

  useEffect(() => {
    taxiCorporationList.load({}, taxiCorporationListAbort.signal);
  }, []);

  const [isContractAddPending, setIsContractAddPending] = useState(false);

  const onContractAddSuccess = () => {
    notificationService.success(translations.successAddNotificationText);
    navigate(
      cargoRoutesHelper.getTaxiContractListingRoute({
        cargoCompanyUuid: cargoCompanyUuid!,
      }),
    );
  };

  const onContractAddFailure = () => {
    notificationService.error(translations.failureAddNotificationText);
  };

  const addContract = async () => {
    setIsContractAddPending(true);

    const params: ContractsCargoTaxiAddParams =
      cargoTaxiContractAddParamsFactory.create(form.values, cargoCompanyUuid!);

    try {
      await contractsService.addCargoTaxiContract(
        params,
        cargoTaxiContractAddAbort.signal,
      );

      onContractAddSuccess();
    } catch {
      onContractAddFailure();
    } finally {
      setIsContractAddPending(false);
    }
  };

  const onSubmitButtonClick = async () => {
    const isFormValid = await form.validateAll();

    if (!isFormValid) {
      return;
    }

    addContract();
  };

  return (
    <>
      <HeadingComponent title={translations.header.headingText} />
      <Row>
        <Column xl={8}>
          <CardComponent>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.taxiCorporationLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.taxiCorporationUuid.errorMessage
                  }
                >
                  <TaxiCorporationSingleSelectComponent
                    placeholder={translations.form.taxiCorporationPlaceholder}
                    selectedTaxiCorporationUuid={
                      form.values.taxiCorporationUuid
                    }
                    onChange={(value) =>
                      form.setValue("taxiCorporationUuid", value?.uuid ?? "")
                    }
                    isError={
                      !!form.validationResults.taxiCorporationUuid.errorMessage
                    }
                    taxiCorporationList={taxiCorporationList.data.data}
                    isLoading={taxiCorporationList.isLoading}
                    isDisabled={taxiCorporationList.isError}
                    onBlur={() => form.validate("taxiCorporationUuid")}
                    idForTesting="cargo-taxi-contract-add-taxi-corporation"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.periodOfValidityLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.periodOfValidity.errorMessage
                  }
                >
                  <DateRangeInputComponent
                    placeholder={translations.form.periodOfValidityPlaceholder}
                    date={form.values.periodOfValidity}
                    onChange={(value) => {
                      form.setValue("periodOfValidity", value);
                      form.validate("periodOfValidity", value);
                    }}
                    hasError={
                      !!form.validationResults.periodOfValidity.errorMessage
                    }
                    idForTesting="cargo-taxi-contract-add-period-validity"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.billingModelLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.billingModel.errorMessage
                  }
                >
                  <BillingModelSingleSelectComponent
                    placeholder={translations.form.billingModelPlaceholder}
                    value={form.values.billingModel}
                    onChange={(billingModel) =>
                      form.setValue("billingModel", billingModel)
                    }
                    isError={!!form.validationResults.billingModel.errorMessage}
                    onBlur={() => form.validate("billingModel")}
                    idForTesting="cargo-taxi-contract-add-billing-model"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.distanceRateLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.distanceRate.errorMessage
                  }
                >
                  <NumericInputComponent
                    placeholder={translations.form.distanceRatePlaceholder}
                    value={form.values.distanceRate}
                    onChange={(value) => form.setValue("distanceRate", value)}
                    hasError={
                      !!form.validationResults.distanceRate.errorMessage
                    }
                    onBlur={() => form.validate("distanceRate")}
                    decimalPrecision={2}
                    idForTesting="cargo-taxi-contract-add-distance-rate"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.discountPercentLimitLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.discountPercentLimit.errorMessage
                  }
                >
                  <NumericInputComponent
                    placeholder={
                      translations.form.discountPercentLimitPlaceholder
                    }
                    value={form.values.discountPercentLimit}
                    onChange={(value) =>
                      form.setValue("discountPercentLimit", value)
                    }
                    hasError={
                      !!form.validationResults.discountPercentLimit.errorMessage
                    }
                    onBlur={() => form.validate("discountPercentLimit")}
                    decimalPrecision={2}
                    idForTesting="cargo-taxi-contract-add-discount"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.freeStoppingPeriodLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.freeStoppingPeriodMinutes
                      .errorMessage
                  }
                >
                  <NumericInputComponent
                    placeholder={
                      translations.form.freeStoppingPeriodPlaceholder
                    }
                    value={form.values.freeStoppingPeriodMinutes}
                    onChange={(value) =>
                      form.setValue("freeStoppingPeriodMinutes", value)
                    }
                    hasError={
                      !!form.validationResults.freeStoppingPeriodMinutes
                        .errorMessage
                    }
                    onBlur={() => form.validate("freeStoppingPeriodMinutes")}
                    isIntegerOnly
                    idForTesting="cargo-taxi-contract-add-free-stopping-period"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.stoppingRateLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.stoppingRate.errorMessage
                  }
                >
                  <NumericInputComponent
                    placeholder={translations.form.stoppingRatePlaceholder}
                    value={form.values.stoppingRate}
                    onChange={(value) => form.setValue("stoppingRate", value)}
                    hasError={
                      !!form.validationResults.stoppingRate.errorMessage
                    }
                    onBlur={() => form.validate("stoppingRate")}
                    decimalPrecision={2}
                    idForTesting="cargo-taxi-contract-add-stopping-rate"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.tollRoadsDuringOrderAllowedLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.isTollRoadsDuringOrderAllowed
                      .errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.tollRoadsDuringOrderAllowedPlaceholder
                    }
                    value={form.values.isTollRoadsDuringOrderAllowed}
                    onChange={(value) =>
                      form.setValue("isTollRoadsDuringOrderAllowed", value)
                    }
                    isError={
                      !!form.validationResults.isTollRoadsDuringOrderAllowed
                        .errorMessage
                    }
                    onBlur={() =>
                      form.validate("isTollRoadsDuringOrderAllowed")
                    }
                    idForTesting="cargo-taxi-contract-add-toll-roads-during-order"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={
                    translations.form.tollRoadsWhileApproachingAllowedLabel
                  }
                  isRequired
                  errorMessage={
                    form.validationResults.isTollRoadsWhileArrivalAllowed
                      .errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form
                        .tollRoadsWhileApproachingAllowedPlaceholder
                    }
                    value={form.values.isTollRoadsWhileArrivalAllowed}
                    onChange={(value) =>
                      form.setValue("isTollRoadsWhileArrivalAllowed", value)
                    }
                    isError={
                      !!form.validationResults.isTollRoadsWhileArrivalAllowed
                        .errorMessage
                    }
                    onBlur={() =>
                      form.validate("isTollRoadsWhileArrivalAllowed")
                    }
                    idForTesting="cargo-taxi-contract-add-toll-roads-while-arrival"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.tollRoadsWhileReturningAllowedLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.isTollRoadsWhileReturningAllowed
                      .errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form
                        .tollRoadsWhileReturningAllowedPlaceholder
                    }
                    value={form.values.isTollRoadsWhileReturningAllowed}
                    onChange={(value) =>
                      form.setValue("isTollRoadsWhileReturningAllowed", value)
                    }
                    isError={
                      !!form.validationResults.isTollRoadsWhileReturningAllowed
                        .errorMessage
                    }
                    onBlur={() =>
                      form.validate("isTollRoadsWhileReturningAllowed")
                    }
                    idForTesting="cargo-taxi-contract-add-toll-roads-while-returning"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.orderPublishingAllowedLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.isOrderPublishingAllowed.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.orderPublishingAllowedPlaceholder
                    }
                    value={form.values.isOrderPublishingAllowed}
                    onChange={(value) =>
                      form.setValue("isOrderPublishingAllowed", value)
                    }
                    isError={
                      !!form.validationResults.isOrderPublishingAllowed
                        .errorMessage
                    }
                    onBlur={() => form.validate("isOrderPublishingAllowed")}
                    idForTesting="cargo-taxi-contract-add-order-publishing"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.contactPassengerAfterMinutesLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.contactPassengerAfterMinutes
                      .errorMessage
                  }
                >
                  <NumericInputComponent
                    placeholder={
                      translations.form.contactPassengerAfterMinutesPlaceholder
                    }
                    value={form.values.contactPassengerAfterMinutes}
                    onChange={(value) =>
                      form.setValue("contactPassengerAfterMinutes", value)
                    }
                    hasError={
                      !!form.validationResults.contactPassengerAfterMinutes
                        .errorMessage
                    }
                    isIntegerOnly
                    onBlur={() => form.validate("contactPassengerAfterMinutes")}
                    idForTesting="cargo-taxi-contract-add-contact-passenger-after-minutes"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.cancelOrderByTaxiAllowedLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.cancelOrderByTaxiAllowed.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.cancelOrderByTaxiAllowedPlaceholder
                    }
                    value={form.values.cancelOrderByTaxiAllowed}
                    onChange={(value) =>
                      form.setValue("cancelOrderByTaxiAllowed", value)
                    }
                    isError={
                      !!form.validationResults.cancelOrderByTaxiAllowed
                        .errorMessage
                    }
                    onBlur={() => form.validate("cancelOrderByTaxiAllowed")}
                    idForTesting="cargo-taxi-contract-add-cancel-order-by-taxi-allowed"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.editOrderByTaxiAllowedLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.editOrderByTaxiAllowed.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.editOrderByTaxiAllowedPlaceholder
                    }
                    value={form.values.editOrderByTaxiAllowed}
                    onChange={(value) =>
                      form.setValue("editOrderByTaxiAllowed", value)
                    }
                    isError={
                      !!form.validationResults.editOrderByTaxiAllowed
                        .errorMessage
                    }
                    onBlur={() => form.validate("editOrderByTaxiAllowed")}
                    idForTesting="cargo-taxi-contract-add-edit-order-by-taxi-allowed"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={12}>
                <FormFieldComponent
                  label={translations.form.isActiveLabel}
                  isRequired
                  errorMessage={form.validationResults.isActive.errorMessage}
                >
                  <BooleanSelectComponent
                    placeholder={translations.form.isActivePlaceholder}
                    value={form.values.isActive}
                    onChange={(value) => form.setValue("isActive", value)}
                    isError={!!form.validationResults.isActive.errorMessage}
                    onBlur={() => form.validate("isActive")}
                    idForTesting="cargo-taxi-contract-add-is-active"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
          </CardComponent>
          <CardComponent
            header={{ title: translations.form.autoApprovalHeaderTitle }}
          >
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.autoApprovalSwitchLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.autoApprovalSwitch.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.autoApprovalSwitchPlaceholder
                    }
                    value={form.values.autoApprovalSwitch}
                    onChange={(value) =>
                      form.setValue("autoApprovalSwitch", value)
                    }
                    isError={
                      !!form.validationResults.autoApprovalSwitch.errorMessage
                    }
                    onBlur={() => {
                      form.validate("autoApprovalSwitch");
                      form.validate("autoApprovalMinDistance");
                      form.validate("autoApprovalMinTime");
                    }}
                    idForTesting="cargo-taxi-contract-add-auto-approval-switch"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.autoApprovalMinDistanceLabel}
                  isRequired={!!form.values.autoApprovalSwitch}
                  errorMessage={
                    form.validationResults.autoApprovalMinDistance.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={form.values.autoApprovalMinDistance}
                    placeholder={
                      translations.form.autoApprovalMinDistancePlaceholder
                    }
                    onChange={(value) =>
                      form.setValue("autoApprovalMinDistance", value)
                    }
                    hasError={
                      !!form.validationResults.autoApprovalMinDistance
                        .errorMessage
                    }
                    onBlur={() => form.validate("autoApprovalMinDistance")}
                    isDisabled={!form.values.autoApprovalSwitch}
                    isIntegerOnly
                    idForTesting="cargo-taxi-contract-add-auto-approval-min-distance"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.autoApprovalMinTimeLabel}
                  isRequired={!!form.values.autoApprovalSwitch}
                  errorMessage={
                    form.validationResults.autoApprovalMinTime.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={form.values.autoApprovalMinTime}
                    placeholder={
                      translations.form.autoApprovalMinTimePlaceholder
                    }
                    onChange={(value) => {
                      form.setValue("autoApprovalMinTime", value);
                    }}
                    hasError={
                      !!form.validationResults.autoApprovalMinTime.errorMessage
                    }
                    onBlur={() => form.validate("autoApprovalMinTime")}
                    isDisabled={!form.values.autoApprovalSwitch}
                    isIntegerOnly
                    idForTesting="cargo-taxi-contract-add-auto-approval-min-time"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
          </CardComponent>
          <CardComponent
            header={{ title: translations.form.approvalDeadlineHeaderTitle }}
          >
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.approvalDeadlineSwitchLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.approvalDeadlineSwitch.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.approvalDeadlineSwitchPlaceholder
                    }
                    value={form.values.approvalDeadlineSwitch}
                    onChange={(value) =>
                      form.setValue("approvalDeadlineSwitch", value)
                    }
                    isError={
                      !!form.validationResults.approvalDeadlineSwitch
                        .errorMessage
                    }
                    onBlur={() => {
                      form.validate("approvalDeadlineSwitch");
                      form.validate("approvalDeadlineTime");
                    }}
                    idForTesting="cargo-taxi-contract-add-approval-deadline-switch"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.approvalDeadlineTimeLabel}
                  isRequired={!!form.values.approvalDeadlineSwitch}
                  errorMessage={
                    form.validationResults.approvalDeadlineTime.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={form.values.approvalDeadlineTime}
                    placeholder={
                      translations.form.approvalDeadlineTimePlaceholder
                    }
                    onChange={(value) => {
                      form.setValue("approvalDeadlineTime", value);
                    }}
                    hasError={
                      !!form.validationResults.approvalDeadlineTime.errorMessage
                    }
                    onBlur={() => form.validate("approvalDeadlineTime")}
                    isDisabled={!form.values.approvalDeadlineSwitch}
                    isIntegerOnly
                    idForTesting="cargo-taxi-contract-add-approval-deadline-time"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
          </CardComponent>
          <ButtonComponent
            onClick={onSubmitButtonClick}
            type="primary"
            isLoading={isContractAddPending}
            idForTesting="cargo-taxi-contract-add-submit-button"
            classNames={{ root: "mt-4" }}
            title={translations.form.submitButtonTitle}
          >
            {translations.form.submitButtonText}
          </ButtonComponent>
        </Column>
      </Row>
    </>
  );
};

export default CargoTaxiContractAddComponent;
